import FetchWrapper from './api.js'
import { getAuth } from 'firebase/auth'
import { Capacitor } from '@capacitor/core'
import { useUserStore } from '@/stores/user'

const platform = Capacitor.getPlatform()
const SERVICE_BASE_URI = process.env.NODE_ENV === 'development' ? (platform === 'android' ? 'http://10.0.2.2:5000/' : process.env.VUE_APP_SERVICE_API_URI_DEV) : process.env.VUE_APP_SERVICE_API_URI
const SERVICE_USER_URI = SERVICE_BASE_URI + 'users/'

export class UserServiceAPI extends FetchWrapper {
  constructor() {
    super(SERVICE_USER_URI, FetchWrapper.CONTENT_TYPE_JSON, FetchWrapper.AUTH_SCHEME_NONE)

    const userId = this.getUserId()
    if (!userId) {
      throw new Error("No user ID")
    }
    this.baseURI = this.baseURI + userId + '/'
  }

  getAccessToken() {
    return process.env.VUE_APP_SERVICE_API_KEY
  }

  async onError(error) {
    throw error
  }

  getUserId() {
    const userStore = useUserStore()
    const user = userStore.user
    const admin = userStore.admin

    // If admin is enabled, get the user ID from user store.
    if (admin) {
      return user?.id
    } else {
      const auth = getAuth()
      return auth.currentUser?.uid
    }
  }

  // GET /users/<user_uid>/
  async getUser() {
    return this.get('')
      .then(data => {
        return data.user
      })
  }

  // PUT /users/<user_uid>/goal/
  async updateGoal(goal) {
    const data = {
      goal: goal
    }
    return this.put('goal/', data)
      .then(data => {
        return data.user
      })
  }

  // PUT /users/<user_uid>/purpose/
  async updatePurpose(purpose) {
    const data = {
      purpose: purpose
    }
    return this.put('purpose/', data)
      .then(data => {
        return data.user
      })
  }

  async getTarget() {
    return this.get('targets/')
      .then(data => {
        return {
          id: data.id,
          duration: data.sleep_duration,
          bedTime: data.bed_time,
          wakeTime: data.wake_time,
        }
      })
      .catch((error) => {
        if (error.status === 404) {
          return null
        }
      })
  }

  async postTarget(duration, bedTime, wakeTime) {
    const data = {
      ...(duration && {'sleep_duration': duration}),
      ...(bedTime && {'bed_time': bedTime}),
      ...(wakeTime && {'wake_time': wakeTime}),
    }
    return this.post('targets/', data)
      .then(data => {
        return {
          id: data.id,
          duration: data.sleep_duration,
          bedTime: data.bed_time,
          wakeTime: data.wake_time,
        }
      })
  }

  async putTarget(id, duration, bedTime, wakeTime) {
    const data = {
      ...(duration && {'sleep_duration': duration}),
      ...(bedTime && {'bed_time': bedTime}),
      ...(wakeTime && {'wake_time': wakeTime}),
    }
    return this.put('targets/' + id + '/', data)
      .then(data => {
        return data
      })
  }

  // eslint-disable-next-line
  async getSleepLogDates(dateFrom, dateTo) {}

  // eslint-disable-next-line
  async getSleepLogList(dateFrom) {}

  async getSleepConsistency(dateFrom, dateTo) {
    const params = '?date_from=' + dateFrom + '&date_to=' + dateTo
    return this.get('sleeps/consistency/' + params)
      .then(data => {
        return {
          score: data.score,
          bedTimeRange: data.bed_time_range,
          bedTimeAverage: data.bed_time_average,
        }
      })
      .catch((error) => {
        // TODO: Log error as a warning, rather than exception.
        if (error.status === 404) {
          return null
        }
      })
  }

  async getSleepDurationAverage(dateFrom, dateTo) {
    const params = '?date_from=' + dateFrom + '&date_to=' + dateTo
    return this.get('sleeps/duration/average/' + params)
      .then(data => {
        return data.duration
      })
  }

  async sleepSync(d) {
    return this.put('sleeps/sync/', { date: d })
      .then(data => {
        return data
      })
      .catch(() => {
        // TODO: Log error as a warning, rather than exception.
        return null
      })
  }

  async getStreak(id) {
    // GET `/user/{uuid}/streak/`
    const streakId = id ? id + '/' : ''
    return this.get('streaks/' + streakId)
      .then(data => {
        return data
      })
      .catch(() => {
        // TODO: Log error as a warning, rather than exception.
        return null
      })
  }

  async createStreak(date, days) {
    // POST `/user/{uuid}/streak/`
    // TODO: Implement streak type properly.
    // await streakTypes = this.post('/streaks/type')
    // console.log('streakTypes:', streakTypes)

    const data = {
      start_date: date,
      type: 1,
      ...(days && {'d': days}),
    }
    return this.post('streaks/', data)
      .then(data => {
        return data
      })
  }

  async deactivateStreak(streakId) {
    // PUT `user/{uuid}/streak/{id}/deactivate/`
    return this.put('streaks/' + streakId + '/deactivate/')
      .then(data => {
        return data
      })
  }

  // GET `/users/{uuid}/tools/{id}/?category={category}`
  async getUserTools(toolId = null) {
    return this.get('tools/' + (toolId === null ? '' : (toolId + '/')))
      .then(data => {
        return data.tools
      })
  }

  // PUT `/users/{uuid}/tools/{id}/`
  // state
  async updateUserTool(toolId, state = null) {
    const data = {
      state: state,
    }
    return this.put('tools/' + toolId + '/', data)
      .then(data => {
        return data.tool
      })
  }

  // PUT `/users/{uuid}/tools/unlock/`
  async unlockUserTool() {
    return this.put('tools/unlock/')
      .then(data => {
        return data.tool
      })
  }

  // GET `/users/{uid}/plans/
  async getPlan() {
    return this.get('plans/')
      .then(data => {
        return data
      })
      .catch((error) => {
        if (error.status === 404) {
          return null
        }
      })
  }

  // POST `/users/{uid}/plans/
  async createPlan() {
    return this.post('plans/')
      .then(data => {
        return data
      })
  }

  // `PATCH /users/{uid}/plans/repeat/`
  async stageRetry(planId) {
    return this.patch('plans/' + planId + '/repeat/')
      .then(data => {
        return data
      })
  }

  // `PATCH /users/{uid}/plans/advance/`
  async stageAdvance(planId) {
    return this.patch('plans/' + planId + '/advance/')
      .then(data => {
        return data
      })
  }

  // `PATCH /users/{uid}/plans/{id}/deactivate/`
  async deactivatePlan(planId) {
    return this.put('plans/' + planId + '/deactivate/')
      .then(data => {
        return data
      })
  }
}

export class ServiceAPI extends FetchWrapper {
  constructor() {
    super(SERVICE_BASE_URI, FetchWrapper.CONTENT_TYPE_JSON, FetchWrapper.AUTH_SCHEME_NONE)
  }

  getAccessToken() {
    return process.env.VUE_APP_SERVICE_API_KEY
  }

  async onError(error) {
    throw error
  }

  async getTools() {
    // GET `/tools/`
    return this.get('tools/')
      .then(data => {
        return data.tools
      })
  }
}
